import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  EditorContent,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  useEditor,
  type NodeViewProps,
} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import { NodeSelection } from "@tiptap/pm/state";
import { ASSETS_BASE_URL } from "../utils/constants.ts";
import { appRepo } from "../repo/app-repo.ts";

type ImageAlign = "left" | "center" | "right";

function normalizeWidth(width: string | number | null | undefined): string | null {
  if (width === null || width === undefined || width === "") return null;
  const value = String(width).trim();
  if (!value) return null;
  if (value.endsWith("%")) return value;
  const px = parseInt(value, 10);
  return Number.isFinite(px) && px > 0 ? String(px) : null;
}

function widthCss(width: string | null): string | undefined {
  if (!width) return undefined;
  return width.endsWith("%") ? width : `${width}px`;
}

function alignMargins(align: ImageAlign): { marginLeft: string; marginRight: string } {
  if (align === "left") return { marginLeft: "0", marginRight: "auto" };
  if (align === "right") return { marginLeft: "auto", marginRight: "0" };
  return { marginLeft: "auto", marginRight: "auto" };
}

function buildImageStyle(width: string | null, align: ImageAlign): string {
  const parts = ["height: auto", "max-width: 100%", "display: block"];
  parts.push(`width: ${widthCss(width) ?? "auto"}`);
  const margins = alignMargins(align);
  parts.push(`margin-left: ${margins.marginLeft}`, `margin-right: ${margins.marginRight}`);
  return parts.join("; ");
}

function parseAlignFromElement(element: HTMLElement): ImageAlign {
  const dataAlign = element.getAttribute("data-align");
  if (dataAlign === "left" || dataAlign === "center" || dataAlign === "right") return dataAlign;
  const style = element.getAttribute("style") || "";
  const marginLeft = /margin-left:\s*auto/i.test(style);
  const marginRight = /margin-right:\s*auto/i.test(style);
  if (marginLeft && marginRight) return "center";
  if (marginLeft) return "right";
  if (marginRight) return "left";
  return "center";
}

function parseWidthFromElement(element: HTMLElement): string | null {
  const dataWidth = element.getAttribute("data-width");
  if (dataWidth) return normalizeWidth(dataWidth);
  const attrWidth = element.getAttribute("width");
  if (attrWidth) return normalizeWidth(attrWidth);
  const styleWidth = element.style.width;
  if (styleWidth) return normalizeWidth(styleWidth);
  return null;
}

function ResizableImageView({ node, updateAttributes, selected }: NodeViewProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  const align = (node.attrs.align as ImageAlign) || "center";
  const width = normalizeWidth(node.attrs.width);
  const boxWidth = widthCss(width);
  const margins = alignMargins(align);

  const onResizeStart = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      const startX = event.clientX;
      const startWidth = boxRef.current?.getBoundingClientRect().width ?? 0;
      const maxWidth =
        boxRef.current?.closest(".html-editor-content")?.clientWidth ??
        boxRef.current?.parentElement?.clientWidth ??
        1200;

      const onMove = (moveEvent: MouseEvent) => {
        const next = Math.min(maxWidth, Math.max(40, Math.round(startWidth + (moveEvent.clientX - startX))));
        updateAttributes({ width: String(next) });
      };
      const onUp = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
      };
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [updateAttributes]
  );

  return (
    <NodeViewWrapper className="html-editor-image-wrap" data-align={align} as="div">
      <div
        ref={boxRef}
        className={`html-editor-image-box${selected ? " is-selected" : ""}`}
        style={{
          width: boxWidth ?? "fit-content",
          maxWidth: "100%",
          marginTop: "0.75rem",
          marginBottom: "0.75rem",
          marginLeft: margins.marginLeft,
          marginRight: margins.marginRight,
        }}
      >
        <img
          src={node.attrs.src}
          alt={node.attrs.alt || ""}
          className="html-editor-image"
          draggable={false}
          style={{
            width: boxWidth ? "100%" : "auto",
            maxWidth: "100%",
            height: "auto",
            display: "block",
          }}
        />
        {selected ? (
          <span
            className="html-editor-image-resize-handle"
            title="کشیدن برای تغییر اندازه"
            onMouseDown={onResizeStart}
          />
        ) : null}
      </div>
    </NodeViewWrapper>
  );
}

const ResizableImage = Image.extend({
  selectable: true,
  draggable: true,
  addAttributes() {
    return {
      ...this.parent?.(),
      alt: {
        default: "",
        parseHTML: (element) => element.getAttribute("alt") ?? "",
        renderHTML: (attributes) => ({ alt: attributes.alt || "" }),
      },
      width: {
        default: null,
        parseHTML: (element) => parseWidthFromElement(element as HTMLElement),
        renderHTML: (attributes) => {
          const width = normalizeWidth(attributes.width);
          if (!width) return {};
          if (width.endsWith("%")) return { "data-width": width };
          return { width };
        },
      },
      align: {
        default: "center",
        parseHTML: (element) => parseAlignFromElement(element as HTMLElement),
        renderHTML: (attributes) => ({
          "data-align": (attributes.align as ImageAlign) || "center",
        }),
      },
    };
  },
  renderHTML({ node, HTMLAttributes }) {
    const normalizedWidth = normalizeWidth(node.attrs.width);
    const imageAlign = (node.attrs.align as ImageAlign) || "center";
    const rest = { ...HTMLAttributes } as Record<string, string>;
    delete rest.style;
    delete rest.width;
    delete rest["data-width"];
    delete rest["data-align"];
    delete rest.align;
    const attrs: Record<string, string> = {
      ...rest,
      class: ["html-editor-image", rest.class].filter(Boolean).join(" "),
      "data-align": imageAlign,
      style: buildImageStyle(normalizedWidth, imageAlign),
    };
    if (normalizedWidth && !normalizedWidth.endsWith("%")) {
      attrs.width = normalizedWidth;
    } else if (normalizedWidth) {
      attrs["data-width"] = normalizedWidth;
    }
    return ["img", attrs];
  },
  addNodeView() {
    return ReactNodeViewRenderer(ResizableImageView);
  },
}).configure({
  inline: false,
  allowBase64: false,
  HTMLAttributes: { class: "html-editor-image" },
});

type Props = {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: string;
};

/** مسیر نسبی ذخیره‌شده را برای نمایش در ادیتور به آدرس کامل تبدیل می‌کند. */
function toDisplaySrc(src: string): string {
  if (!src) return src;
  if (src.startsWith("http") || src.startsWith("data:") || src.startsWith("blob:")) return src;
  return `${ASSETS_BASE_URL}${src.replace(/^\//, "")}`;
}

/** آدرس کامل assets را برای ارسال به سرور به مسیر نسبی برمی‌گرداند. */
function toStorageSrc(src: string): string {
  if (!src) return src;
  if (src.startsWith(ASSETS_BASE_URL)) return src.slice(ASSETS_BASE_URL.length);
  return src;
}

function rewriteImgSrcs(html: string, mapSrc: (src: string) => string): string {
  if (!html) return html;
  const doc = new DOMParser().parseFromString(html, "text/html");
  doc.querySelectorAll("img").forEach((img) => {
    const src = img.getAttribute("src");
    if (src) img.setAttribute("src", mapSrc(src));
  });
  return doc.body.innerHTML;
}

function htmlToDisplay(html: string): string {
  return rewriteImgSrcs(html, toDisplaySrc);
}

function htmlToStorage(html: string): string {
  return rewriteImgSrcs(html, toStorageSrc);
}

function normalizeEditorHtml(html: string): string {
  return html === "<p></p>" ? "" : html;
}

type TextStats = { words: number; letters: number };

const EMPTY_TEXT_STATS: TextStats = { words: 0, letters: 0 };

/** متن قابل‌مشاهده را بدون تگ HTML به تعداد کلمه و حرف تبدیل می‌کند. */
function getPlainTextStats(text: string): TextStats {
  const plain = text.replace(/\u00a0/g, " ");
  if (!plain.trim()) return EMPTY_TEXT_STATS;

  const words = plain
    .trim()
    .split(/\s+/)
    .filter((token) => /[\p{L}\p{N}]/u.test(token)).length;
  const letters = plain.match(/\p{L}/gu)?.length ?? 0;

  return { words, letters };
}

function formatFaNumber(value: number): string {
  return value.toLocaleString("fa-IR");
}

function ToolbarButton({
  onClick,
  active,
  disabled,
  title,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={`rounded px-2 py-1 text-xs transition disabled:cursor-not-allowed disabled:opacity-40 ${
        active
          ? "bg-indigo-500/30 text-indigo-200"
          : "text-slate-300 hover:bg-slate-800 hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

function ToolbarDivider() {
  return <span className="mx-0.5 h-5 w-px bg-slate-700" aria-hidden />;
}

function promptAltText(previous = ""): string | null {
  const alt = window.prompt("متن جایگزین تصویر (alt) را وارد کنید:", previous);
  if (alt === null) return null;
  return alt.trim();
}

export default function HtmlEditor({
  value,
  onChange,
  placeholder = "متن را وارد کنید...",
  minHeight = "220px",
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const importFileInputRef = useRef<HTMLInputElement>(null);
  const lastEmitted = useRef(value);
  const [uploading, setUploading] = useState(false);
  const [textStats, setTextStats] = useState<TextStats>(EMPTY_TEXT_STATS);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
      }),
      Underline,
      TextStyle,
      Color,
      ResizableImage,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { class: "html-editor-link", rel: "noopener noreferrer", target: "_blank" },
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Placeholder.configure({ placeholder }),
    ],
    content: htmlToDisplay(value || ""),
    onCreate: ({ editor: ed }) => {
      setTextStats(getPlainTextStats(ed.getText()));
    },
    editorProps: {
      attributes: {
        class: "html-editor-content outline-none",
        style: `min-height: ${minHeight}`,
      },
      handleClickOn: (view, _pos, node, nodePos, _event, direct) => {
        if (!direct || node.type.name !== "image") return false;
        view.dispatch(view.state.tr.setSelection(NodeSelection.create(view.state.doc, nodePos)));
        return true;
      },
      handleKeyDown: (view, event) => {
        if (event.key !== "Backspace" && event.key !== "Delete") return false;
        const { selection } = view.state;
        if (!(selection instanceof NodeSelection) || selection.node.type.name !== "image") {
          return false;
        }
        view.dispatch(view.state.tr.deleteSelection().scrollIntoView());
        return true;
      },
    },
    onUpdate: ({ editor: ed }) => {
      const storageHtml = normalizeEditorHtml(htmlToStorage(ed.getHTML()));
      lastEmitted.current = storageHtml;
      setTextStats(getPlainTextStats(ed.getText()));
      onChange(storageHtml);
    },
  });

  useEffect(() => {
    if (!editor) return;
    const incoming = value || "";
    if (incoming === lastEmitted.current) return;
    editor.commands.setContent(htmlToDisplay(incoming), { emitUpdate: false });
    lastEmitted.current = incoming;
    setTextStats(getPlainTextStats(editor.getText()));
  }, [value, editor]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const previous = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("آدرس لینک را وارد کنید:", previous || "https://");
    if (url === null) return;
    if (!url.trim()) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run();
  }, [editor]);

  const insertImageByUrl = useCallback(() => {
    if (!editor) return;
    const url = window.prompt("آدرس تصویر را وارد کنید:", "https://");
    if (!url?.trim()) return;
    const alt = promptAltText();
    if (alt === null) return;
    editor.chain().focus().setImage({ src: toDisplaySrc(url.trim()), alt }).run();
  }, [editor]);

  const editImageAlt = useCallback(() => {
    if (!editor || !editor.isActive("image")) {
      window.alert("ابتدا یک تصویر را در ادیتور انتخاب کنید.");
      return;
    }
    const previous = (editor.getAttributes("image").alt as string | undefined) || "";
    const alt = promptAltText(previous);
    if (alt === null) return;
    editor.chain().focus().updateAttributes("image", { alt }).run();
  }, [editor]);

  const deleteImage = useCallback(() => {
    if (!editor) return;
    if (editor.isActive("image")) {
      editor.chain().focus().deleteSelection().run();
      return;
    }
    // اگر کرسر کنار تصویر باشد، تصویر قبلی/بعدی را حذف کن
    const { state } = editor;
    const { $from } = state.selection;
    const nodeBefore = $from.nodeBefore;
    const nodeAfter = $from.nodeAfter;
    if (nodeBefore?.type.name === "image") {
      const from = $from.pos - nodeBefore.nodeSize;
      editor.chain().focus().deleteRange({ from, to: $from.pos }).run();
      return;
    }
    if (nodeAfter?.type.name === "image") {
      const to = $from.pos + nodeAfter.nodeSize;
      editor.chain().focus().deleteRange({ from: $from.pos, to }).run();
      return;
    }
    window.alert("ابتدا یک تصویر را در ادیتور انتخاب کنید.");
  }, [editor]);

  const setImageWidth = useCallback(
    (width: string | null) => {
      if (!editor || !editor.isActive("image")) {
        window.alert("ابتدا یک تصویر را در ادیتور انتخاب کنید.");
        return;
      }
      editor.chain().focus().updateAttributes("image", { width }).run();
    },
    [editor]
  );

  const setImageWidthCustom = useCallback(() => {
    if (!editor || !editor.isActive("image")) {
      window.alert("ابتدا یک تصویر را در ادیتور انتخاب کنید.");
      return;
    }
    const previous = normalizeWidth(editor.getAttributes("image").width) || "100%";
    const input = window.prompt("عرض تصویر را وارد کنید (مثلاً 300 یا 50%):", previous);
    if (input === null) return;
    const width = normalizeWidth(input);
    if (!width) {
      window.alert("مقدار عرض معتبر نیست.");
      return;
    }
    editor.chain().focus().updateAttributes("image", { width }).run();
  }, [editor]);

  const setTextOrImageAlign = useCallback(
    (align: ImageAlign) => {
      if (!editor) return;
      if (editor.isActive("image")) {
        editor.chain().focus().updateAttributes("image", { align }).run();
        return;
      }
      editor.chain().focus().setTextAlign(align).run();
    },
    [editor]
  );

  const imageAlign = (editor?.getAttributes("image").align as ImageAlign | undefined) || "center";
  const imageWidth = normalizeWidth(editor?.getAttributes("image").width);
  const imageSelected = Boolean(editor?.isActive("image"));

  const onPickImageFile = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file || !editor || uploading) return;
      if (!file.type.startsWith("image/")) {
        window.alert("فقط فایل تصویری مجاز است.");
        return;
      }
      const defaultAlt = file.name.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ").trim();
      const alt = promptAltText(defaultAlt);
      if (alt === null) return;

      setUploading(true);
      try {
        const response = await appRepo.uploadImage(file);
        if (!response.status || !response.data) {
          window.alert(response.message || "آپلود تصویر ناموفق بود.");
          return;
        }
        const relativePath = String(response.data.url).replace(/^\//, "");
        editor.chain().focus().setImage({ src: toDisplaySrc(relativePath), alt }).run();
      } catch {
        window.alert("خطا در آپلود تصویر. دوباره تلاش کنید.");
      } finally {
        setUploading(false);
      }
    },
    [editor, uploading]
  );

  const exportContent = useCallback(() => {
    const html = value || (editor ? normalizeEditorHtml(htmlToStorage(editor.getHTML())) : "");
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    anchor.href = url;
    anchor.download = `content-${stamp}.html`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }, [editor, value]);

  const onImportFile = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) return;

      const current = value || (editor ? normalizeEditorHtml(htmlToStorage(editor.getHTML())) : "");
      if (current.trim() && !window.confirm("محتوای فعلی ادیتور جایگزین شود؟")) {
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const text = String(reader.result || "");
        const storageHtml = normalizeEditorHtml(htmlToStorage(text));
        lastEmitted.current = storageHtml;
        if (editor) {
          editor.commands.setContent(htmlToDisplay(storageHtml), { emitUpdate: false });
          setTextStats(getPlainTextStats(editor.getText()));
        }
        onChange(storageHtml);
      };
      reader.onerror = () => {
        window.alert("خواندن فایل ناموفق بود.");
      };
      reader.readAsText(file, "utf-8");
    },
    [editor, onChange, value]
  );

  if (!editor) {
    return (
      <div className="rounded-lg border border-slate-700 bg-slate-950 px-3 py-8 text-sm text-slate-400">
        در حال بارگذاری ادیتور...
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-slate-700 bg-slate-950">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-800 bg-slate-900/80 p-1.5">
        <ToolbarButton
          title="پررنگ"
          active={editor.isActive("bold")}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <strong>B</strong>
        </ToolbarButton>
        <ToolbarButton
          title="ایتالیک"
          active={editor.isActive("italic")}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <em>I</em>
        </ToolbarButton>
        <ToolbarButton
          title="زیرخط"
          active={editor.isActive("underline")}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <span className="underline">U</span>
        </ToolbarButton>
        <ToolbarButton
          title="خط‌خورده"
          active={editor.isActive("strike")}
          onClick={() => editor.chain().focus().toggleStrike().run()}
        >
          <span className="line-through">S</span>
        </ToolbarButton>

        <ToolbarDivider />

        <ToolbarButton
          title="عنوان ۱"
          active={editor.isActive("heading", { level: 1 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        >
          H1
        </ToolbarButton>
        <ToolbarButton
          title="عنوان ۲"
          active={editor.isActive("heading", { level: 2 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          H2
        </ToolbarButton>
        <ToolbarButton
          title="عنوان ۳"
          active={editor.isActive("heading", { level: 3 })}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        >
          H3
        </ToolbarButton>
        <ToolbarButton
          title="پاراگراف"
          active={editor.isActive("paragraph")}
          onClick={() => editor.chain().focus().setParagraph().run()}
        >
          P
        </ToolbarButton>

        <ToolbarDivider />

        <ToolbarButton
          title="لیست نقطه‌ای"
          active={editor.isActive("bulletList")}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          • لیست
        </ToolbarButton>
        <ToolbarButton
          title="لیست شماره‌دار"
          active={editor.isActive("orderedList")}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          ۱. لیست
        </ToolbarButton>
        <ToolbarButton
          title="نقل‌قول"
          active={editor.isActive("blockquote")}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          نقل‌قول
        </ToolbarButton>
        <ToolbarButton
          title="کد"
          active={editor.isActive("codeBlock")}
          onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        >
          {"</>"}
        </ToolbarButton>

        <ToolbarDivider />

        <ToolbarButton
          title="راست‌چین"
          active={imageSelected ? imageAlign === "right" : editor.isActive({ textAlign: "right" })}
          onClick={() => setTextOrImageAlign("right")}
        >
          راست
        </ToolbarButton>
        <ToolbarButton
          title="وسط‌چین"
          active={imageSelected ? imageAlign === "center" : editor.isActive({ textAlign: "center" })}
          onClick={() => setTextOrImageAlign("center")}
        >
          وسط
        </ToolbarButton>
        <ToolbarButton
          title="چپ‌چین"
          active={imageSelected ? imageAlign === "left" : editor.isActive({ textAlign: "left" })}
          onClick={() => setTextOrImageAlign("left")}
        >
          چپ
        </ToolbarButton>
        <ToolbarButton
          title="تراز دوطرفه"
          disabled={imageSelected}
          active={!imageSelected && editor.isActive({ textAlign: "justify" })}
          onClick={() => editor.chain().focus().setTextAlign("justify").run()}
        >
          دوطرفه
        </ToolbarButton>

        <ToolbarDivider />

        <ToolbarButton title="لینک" active={editor.isActive("link")} onClick={setLink}>
          لینک
        </ToolbarButton>
        <ToolbarButton title="تصویر از آدرس" onClick={insertImageByUrl}>
          تصویر URL
        </ToolbarButton>
        <ToolbarButton
          title="آپلود تصویر از فایل"
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploading ? "در حال آپلود..." : "آپلود تصویر"}
        </ToolbarButton>
        <ToolbarButton
          title="ویرایش متن جایگزین تصویر"
          active={imageSelected}
          onClick={editImageAlt}
        >
          alt تصویر
        </ToolbarButton>
        <ToolbarButton
          title="حذف تصویر انتخاب‌شده"
          active={imageSelected}
          onClick={deleteImage}
        >
          حذف تصویر
        </ToolbarButton>
        <ToolbarButton
          title="عرض ۲۵٪"
          active={imageSelected && imageWidth === "25%"}
          onClick={() => setImageWidth("25%")}
        >
          ۲۵٪
        </ToolbarButton>
        <ToolbarButton
          title="عرض ۵۰٪"
          active={imageSelected && imageWidth === "50%"}
          onClick={() => setImageWidth("50%")}
        >
          ۵۰٪
        </ToolbarButton>
        <ToolbarButton
          title="عرض ۷۵٪"
          active={imageSelected && imageWidth === "75%"}
          onClick={() => setImageWidth("75%")}
        >
          ۷۵٪
        </ToolbarButton>
        <ToolbarButton
          title="عرض کامل"
          active={imageSelected && imageWidth === "100%"}
          onClick={() => setImageWidth("100%")}
        >
          ۱۰۰٪
        </ToolbarButton>
        <ToolbarButton title="عرض دلخواه تصویر" active={imageSelected} onClick={setImageWidthCustom}>
          عرض…
        </ToolbarButton>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onPickImageFile}
        />

        <ToolbarDivider />

        <label className="flex items-center gap-1 px-1 text-xs text-slate-400" title="رنگ متن">
          رنگ
          <input
            type="color"
            className="h-6 w-7 cursor-pointer rounded border-0 bg-transparent p-0"
            onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
          />
        </label>
        <ToolbarButton title="حذف رنگ" onClick={() => editor.chain().focus().unsetColor().run()}>
          بی‌رنگ
        </ToolbarButton>

        <ToolbarDivider />

        <ToolbarButton
          title="بازگردانی"
          disabled={!editor.can().undo()}
          onClick={() => editor.chain().focus().undo().run()}
        >
          بازگشت
        </ToolbarButton>
        <ToolbarButton
          title="انجام مجدد"
          disabled={!editor.can().redo()}
          onClick={() => editor.chain().focus().redo().run()}
        >
          جلو
        </ToolbarButton>
        <ToolbarButton
          title="پاک کردن قالب‌بندی"
          onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
        >
          پاک‌سازی
        </ToolbarButton>

        <ToolbarDivider />

        <ToolbarButton title="خروجی گرفتن از محتوا (HTML)" onClick={exportContent}>
          اکسپورت
        </ToolbarButton>
        <ToolbarButton title="ورود محتوا از فایل HTML" onClick={() => importFileInputRef.current?.click()}>
          ایمپورت
        </ToolbarButton>
        <input
          ref={importFileInputRef}
          type="file"
          accept=".html,.htm,.txt,text/html,text/plain"
          className="hidden"
          onChange={onImportFile}
        />
      </div>

      <EditorContent editor={editor} />

      <div className="flex flex-wrap items-center justify-end gap-3 border-t border-slate-800 bg-slate-900/80 px-3 py-1.5 text-xs text-slate-400">
        <span>
          {formatFaNumber(textStats.words)} کلمه
        </span>
        <span className="h-3 w-px bg-slate-700" aria-hidden />
        <span>
          {formatFaNumber(textStats.letters)} حرف
        </span>
      </div>
    </div>
  );
}
